import { MaterialCommunityIcons } from "@expo/vector-icons";
import Constants from "expo-constants";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
    Animated,
    Dimensions,
    Image,
    Pressable,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { fetchRnIdCard } from "@/lib/api";
import { type AuthUser } from "@/lib/auth";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, shadows, spacing, typography } from "@/lib/theme";

const DRAWER_WIDTH = Dimensions.get("window").width * 0.78;

interface Props {
    visible: boolean;
    user: AuthUser | null;
    onClose: () => void;
    onLogout: () => void;
}

function formatPhone(phone: string): string {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === "1") {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
}

export function ProfileDrawer({ visible, user, onClose, onLogout }: Props) {
    const appVersion = Constants.expoConfig?.version ?? "—";
    const router = useRouter();
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);

    // Pull the same display photo the ID card shows (profile avatar → selfie
    // fallback). Best-effort: any failure just leaves the initials avatar.
    useEffect(() => {
        if (!visible || user?.rnId == null || user.rnStatus !== "active") {
            return;
        }
        let cancelled = false;
        fetchRnIdCard()
            .then((res) => {
                if (!cancelled) {
                    setPhotoUrl(res.photoUrl);
                }
            })
            .catch(() => {
                /* keep initials fallback */
            });
        return () => {
            cancelled = true;
        };
    }, [visible, user?.rnId, user?.rnStatus]);

    const handleLogout = useCallback(() => {
        onClose();
        onLogout();
    }, [onClose, onLogout]);

    const handleOpenIdCard = useCallback(() => {
        onClose();
        router.push("/id-card");
    }, [onClose, router]);

    const handleOpenPhones = useCallback(() => {
        onClose();
        router.push("/phones");
    }, [onClose, router]);

    const handleOpenCompliance = useCallback(() => {
        onClose();
        router.push("/compliance");
    }, [onClose, router]);

    const handleOpenTraining = useCallback(() => {
        onClose();
        router.push("/training");
    }, [onClose, router]);

    if (!visible) {
        return null;
    }

    const fullName =
        [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.name || "—";

    return (
        <View style={styles.overlay}>
            {/* Backdrop */}
            <Pressable style={styles.backdrop} onPress={onClose} />

            {/* Drawer panel */}
            <Animated.View style={styles.drawer}>
                {/* Header */}
                <View style={styles.header}>
                    {photoUrl != null ? (
                        <Image
                            {...logRocketRedactProps()}
                            source={{ uri: photoUrl }}
                            style={styles.avatarImage}
                        />
                    ) : (
                        <View style={styles.avatar}>
                            <Text style={styles.avatarText}>
                                {(user?.firstName?.[0] ?? user?.name?.[0] ?? "?").toUpperCase()}
                            </Text>
                        </View>
                    )}
                    <Text style={styles.name}>{fullName}</Text>
                    <Text style={styles.role}>Registered Nurse</Text>
                </View>

                {/* Profile info */}
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Profile</Text>

                    <View style={styles.infoRow}>
                        <MaterialCommunityIcons
                            name="phone-outline"
                            size={18}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.infoText}>
                            {user?.phone ? formatPhone(user.phone) : "—"}
                        </Text>
                    </View>

                    {user?.firstName && (
                        <View style={styles.infoRow}>
                            <MaterialCommunityIcons
                                name="account-outline"
                                size={18}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.infoText}>{fullName}</Text>
                        </View>
                    )}

                    {user?.rnId && (
                        <View style={styles.infoRow}>
                            <MaterialCommunityIcons
                                name="badge-account-outline"
                                size={18}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.infoText}>RN #{user.rnId}</Text>
                        </View>
                    )}

                    {user?.rnStatus === "active" && (
                        <TouchableOpacity
                            style={styles.actionRow}
                            activeOpacity={0.7}
                            onPress={handleOpenIdCard}
                        >
                            <MaterialCommunityIcons
                                name="card-account-details-outline"
                                size={18}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.infoText}>View ID card</Text>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={20}
                                color={colors.textTertiary}
                                style={styles.chevron}
                            />
                        </TouchableOpacity>
                    )}

                    <TouchableOpacity
                        style={styles.actionRow}
                        activeOpacity={0.7}
                        onPress={handleOpenPhones}
                    >
                        <MaterialCommunityIcons
                            name="cellphone"
                            size={18}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.infoText}>Phone numbers</Text>
                        <MaterialCommunityIcons
                            name="chevron-right"
                            size={20}
                            color={colors.textTertiary}
                            style={styles.chevron}
                        />
                    </TouchableOpacity>

                    <TouchableOpacity
                        style={styles.actionRow}
                        activeOpacity={0.7}
                        onPress={handleOpenCompliance}
                    >
                        <MaterialCommunityIcons
                            name="shield-check-outline"
                            size={18}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.infoText}>My Compliance</Text>
                        <MaterialCommunityIcons
                            name="chevron-right"
                            size={20}
                            color={colors.textTertiary}
                            style={styles.chevron}
                        />
                    </TouchableOpacity>

                    <TouchableOpacity
                        style={styles.actionRow}
                        activeOpacity={0.7}
                        onPress={handleOpenTraining}
                    >
                        <MaterialCommunityIcons
                            name="school-outline"
                            size={18}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.infoText}>Training</Text>
                        <MaterialCommunityIcons
                            name="chevron-right"
                            size={20}
                            color={colors.textTertiary}
                            style={styles.chevron}
                        />
                    </TouchableOpacity>
                </View>

                {/* Spacer */}
                <View style={{ flex: 1 }} />

                {/* Footer */}
                <View style={styles.footer}>
                    <TouchableOpacity
                        style={styles.logoutBtn}
                        activeOpacity={0.7}
                        onPress={handleLogout}
                    >
                        <MaterialCommunityIcons name="logout" size={20} color={colors.error500} />
                        <Text style={styles.logoutText}>Log out</Text>
                    </TouchableOpacity>
                    <Text style={styles.version}>v{appVersion}</Text>
                </View>
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFill,
        zIndex: 100,
        flexDirection: "row"
    },
    backdrop: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(15, 10, 40, 0.4)"
    },
    drawer: {
        width: DRAWER_WIDTH,
        height: "100%",
        backgroundColor: colors.surface,
        paddingTop: 60,
        paddingBottom: 34,
        ...shadows.xl
    },
    header: {
        alignItems: "center",
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xl,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    avatar: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: colors.teal50,
        borderWidth: 2,
        borderColor: colors.teal400,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: spacing.md
    },
    avatarText: {
        fontSize: 24,
        fontFamily: fonts.bold,
        color: colors.teal600
    },
    avatarImage: {
        width: 64,
        height: 64,
        borderRadius: 32,
        borderWidth: 2,
        borderColor: colors.teal400,
        marginBottom: spacing.md
    },
    name: {
        ...typography.h2,
        color: colors.textPrimary,
        marginBottom: 2
    },
    role: {
        ...typography.caption,
        color: colors.textSecondary
    },
    section: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.xl
    },
    sectionTitle: {
        ...typography.label,
        color: colors.textTertiary,
        textTransform: "uppercase",
        marginBottom: spacing.base
    },
    infoRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.sm
    },
    actionRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.sm
    },
    chevron: {
        marginLeft: "auto"
    },
    infoText: {
        ...typography.body,
        color: colors.textPrimary
    },
    footer: {
        paddingHorizontal: spacing.lg,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: spacing.base
    },
    logoutBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing.md
    },
    logoutText: {
        ...typography.subtitle,
        color: colors.error500
    },
    version: {
        ...typography.caption,
        color: colors.textTertiary,
        marginTop: spacing.sm
    }
});
